/**
 * Support Widget
 *
 * Floating "contact us" launcher pinned to the bottom-right of every authenticated page.
 * Opening it reveals the support channels configured for the environment
 * (Messenger, Zalo, hotline).
 *
 * Two independent gates, and they answer different questions:
 * - `support_widget_enabled` (Admin → Platform Settings → Feature Flags) — whether the
 *   platform wants the launcher visible at all. Defaults to off; toggling it reaches
 *   clients within ~6 minutes without a deploy.
 * - `config/support.ts` — which channels this environment actually has. A channel with
 *   no value is not rendered, and with no channel at all there is nothing to show.
 *
 * The launcher starts in the bottom-right corner and can be dragged anywhere on the viewport;
 * where the user leaves it is remembered per browser (`useDraggableWidget`).
 */

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ChevronRight, Headset, Phone, X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useTypedTranslation } from "@/hooks/useTypedTranslation";
import { useSupportWidgetEnabled } from "@/hooks/useFeatureFlags";
import { getSupportChannels, type SupportChannelId } from "@/config/support";
import { useDraggableWidget } from "@/hooks/useDraggableWidget";

/** Where the user parked the launcher. Read on mount, written when a drag ends. */
const SUPPORT_WIDGET_POSITION_KEY = "wicloud_support_widget_position";

/** Messenger mark — inline so the widget pulls in no third-party asset. */
function MessengerIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="M12 0C5.373 0 0 4.974 0 11.111c0 3.498 1.744 6.614 4.469 8.654V24l4.088-2.242c1.092.301 2.246.464 3.443.464 6.627 0 12-4.974 12-11.111C24 4.974 18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8.1l3.131 3.26L19.752 8.1l-6.561 6.863z" />
    </svg>
  );
}

/** Zalo mark — the wordmark is the logo, so it is drawn as text rather than traced. */
function ZaloIcon() {
  return (
    <span className="text-[11px] font-bold leading-none tracking-tight">Zalo</span>
  );
}

/**
 * Per-channel presentation. Brand colours are hard-coded on purpose: a channel is
 * recognised by its own colour, so it must not follow the app theme.
 */
const CHANNEL_STYLES: Record<
  SupportChannelId,
  { icon: ReactNode; badgeClass: string; glowClass: string }
> = {
  facebook: {
    icon: <MessengerIcon className="h-[22px] w-[22px]" />,
    badgeClass: "bg-[linear-gradient(135deg,#00B2FF_0%,#006AFF_45%,#FF006E_100%)]",
    glowClass: "group-hover:shadow-[0_6px_16px_-6px_rgba(0,106,255,0.9)]",
  },
  zalo: {
    icon: <ZaloIcon />,
    badgeClass: "bg-[linear-gradient(135deg,#2AA4FF_0%,#0068FF_100%)]",
    glowClass: "group-hover:shadow-[0_6px_16px_-6px_rgba(0,104,255,0.9)]",
  },
  hotline: {
    icon: <Phone className="h-[18px] w-[18px]" />,
    badgeClass: "bg-[linear-gradient(135deg,#34D399_0%,#059669_100%)]",
    glowClass: "group-hover:shadow-[0_6px_16px_-6px_rgba(5,150,105,0.9)]",
  },
};

export function SupportWidget() {
  const { t } = useTypedTranslation("common");
  const enabled = useSupportWidgetEnabled();
  const [open, setOpen] = useState(false);
  const drag = useDraggableWidget<HTMLButtonElement>(SUPPORT_WIDGET_POSITION_KEY);

  // The panel is anchored to the launcher, so dragging with it open would drag the panel too.
  useEffect(() => {
    if (drag.isDragging) {
      setOpen(false);
    }
  }, [drag.isDragging]);

  // Config is read once — it comes from build-time env or the ConfigMap, neither of
  // which changes while the app is running.
  const channels = useMemo(() => getSupportChannels(), []);

  if (!enabled || channels.length === 0) {
    return null;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={drag.ref}
          type="button"
          aria-label={t("support.title")}
          title={t("support.title")}
          style={drag.style}
          onPointerDown={drag.onPointerDown}
          onPointerMove={drag.onPointerMove}
          onPointerUp={drag.onPointerUp}
          onPointerCancel={drag.onPointerCancel}
          onClickCapture={drag.onClickCapture}
          className={cn(
            // Sits above page content but below dialogs and toasts (z-50).
            // `bottom-5 right-5` is only the starting corner — once dragged, the hook takes over
            // placement with inline left/top.
            "group fixed bottom-5 right-5 z-40 inline-flex items-center justify-center",
            // Icon only, at every width — the launcher is a circle, not a labelled pill.
            "h-13 w-13 rounded-full p-1.5",
            "bg-brand text-white",
            "shadow-[0_10px_30px_-10px_var(--brand-strong),0_2px_8px_rgba(0,0,0,0.18)]",
            "transition-[transform,box-shadow] duration-200",
            "hover:-translate-y-0.5 hover:shadow-[0_16px_34px_-10px_var(--brand-strong)]",
            "active:translate-y-0",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
            // Clear of the iOS home indicator.
            "mb-[env(safe-area-inset-bottom)]",
            // A touch drag must move the widget, not scroll the page behind it — and the label
            // must not get text-selected on the way.
            "touch-none select-none",
            drag.isDragging
              // No lift/settle transition while following the pointer, or the widget lags behind it.
              ? "cursor-grabbing transition-none hover:translate-y-0"
              : "cursor-pointer",
          )}
        >
          <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/20">
            {open ? (
              <X className="h-[18px] w-[18px]" />
            ) : (
              <Headset className="h-[18px] w-[18px]" />
            )}
            {/* Availability dot. Hidden while open — the panel already says it. */}
            {!open && (
              <span className="absolute -right-0.5 -top-0.5 flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#34D399] opacity-70" />
                <span className="relative inline-flex h-3 w-3 rounded-full border-2 border-brand bg-[#34D399]" />
              </span>
            )}
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        side="top"
        align="end"
        sideOffset={14}
        className={cn(
          "w-[19rem] overflow-hidden rounded-2xl border-hairline-strong p-0",
          "shadow-[0_24px_48px_-16px_rgba(0,0,0,0.35)]",
        )}
      >
        <header className="border-b border-hairline bg-panel-2 px-4 py-3">
          <p className="text-sm font-semibold tracking-[-0.01em] text-ink">
            {t("support.title")}
          </p>
          <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#34D399]" />
            {t("support.subtitle")}
          </p>
        </header>

        <ul className="flex flex-col gap-0.5 p-2">
          {channels.map((channel) => {
            const style = CHANNEL_STYLES[channel.id];
            const isHotline = channel.id === "hotline";

            return (
              <li key={channel.id}>
                <a
                  href={channel.href}
                  // A `tel:` link must stay in this tab, or the dialer never opens.
                  target={isHotline ? undefined : "_blank"}
                  rel={isHotline ? undefined : "noopener noreferrer"}
                  onClick={() => setOpen(false)}
                  className={cn(
                    "group flex items-center gap-3 rounded-xl px-2.5 py-2.5 cursor-pointer",
                    "transition-colors duration-150 hover:bg-panel-2",
                    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                  )}
                >
                  <span
                    className={cn(
                      "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white",
                      "transition-[transform,box-shadow] duration-200 group-hover:scale-105",
                      style.badgeClass,
                      style.glowClass,
                    )}
                  >
                    {style.icon}
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-ink">
                      {t(`support.channels.${channel.id}.name`)}
                    </span>
                    <span
                      className={cn(
                        "block truncate text-xs text-muted",
                        // The hotline shows the number itself — worth reading, so it
                        // gets the numeric-friendly face rather than the muted caption.
                        channel.detail && "font-mono tracking-tight text-ink-2",
                      )}
                    >
                      {channel.detail ?? t(`support.channels.${channel.id}.description`)}
                    </span>
                  </span>

                  <ChevronRight
                    aria-hidden
                    className={cn(
                      "h-4 w-4 shrink-0 text-muted",
                      "opacity-0 transition-[opacity,transform] duration-200",
                      "group-hover:translate-x-0.5 group-hover:opacity-100",
                    )}
                  />
                </a>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
